import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { authApi } from '../api/client';

export const SetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');

  // Token from URL or from code verification
  const [token, setToken] = useState(tokenFromUrl || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(!!tokenFromUrl);
  const [tokenError, setTokenError] = useState('');
  const [userInfo, setUserInfo] = useState<{ email: string; firstName: string } | null>(null);

  // Code-based flow
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState('');

  // If token in URL, verify it (existing link-based flow)
  useEffect(() => {
    if (!tokenFromUrl) {
      setVerifying(false);
      return;
    }

    authApi.verifyResetToken(tokenFromUrl)
      .then((res) => {
        setUserInfo(res.data.data);
        setToken(tokenFromUrl);
        setVerifying(false);
      })
      .catch((err) => {
        setTokenError(err.response?.data?.error || 'Ugyldigt eller udløbet link.');
        setVerifying(false);
      });
  }, [tokenFromUrl]);

  const handleCodeVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setCodeError('');
    setCodeLoading(true);

    try {
      const res = await authApi.verifyCode(email, code);
      setToken(res.data.data.token);
      setUserInfo({ email: res.data.data.email, firstName: res.data.data.firstName });
    } catch (err: any) {
      setCodeError(err.response?.data?.error || 'Ugyldig kode eller email.');
    } finally {
      setCodeLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Adgangskoden skal være mindst 8 tegn.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Adgangskoderne matcher ikke.');
      return;
    }

    setLoading(true);
    try {
      await authApi.setPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Kunne ikke indstille adgangskode.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center" fontWeight={700}>
            E3-CRM
          </Typography>

          {tokenError ? (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>{tokenError}</Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Har du en kode fra emailen? Indtast den nedenfor:
              </Typography>
              {/* Show code form as fallback when link fails */}
              <form onSubmit={handleCodeVerify}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="8-cifret kode"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  required
                  inputProps={{ maxLength: 8, inputMode: 'numeric', style: { letterSpacing: '4px', fontWeight: 'bold', textAlign: 'center' } }}
                  sx={{ mb: 2 }}
                />
                {codeError && <Alert severity="error" sx={{ mb: 2 }}>{codeError}</Alert>}
                <Button fullWidth type="submit" variant="contained" size="large" disabled={codeLoading || code.length < 8}>
                  {codeLoading ? 'Verificerer...' : 'Verificér kode'}
                </Button>
              </form>
            </>
          ) : success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Din adgangskode er indstillet. Du bliver sendt til login...
            </Alert>
          ) : token && userInfo ? (
            <>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Hej {userInfo.firstName}! Indstil din adgangskode for {userInfo.email}
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Ny adgangskode"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Bekræft adgangskode"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  sx={{ mb: 3 }}
                />
                <Button fullWidth type="submit" variant="contained" size="large" disabled={loading}>
                  {loading ? 'Gemmer...' : 'Indstil adgangskode'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Indtast din email og koden fra invitations-emailen
              </Typography>

              {codeError && <Alert severity="error" sx={{ mb: 2 }}>{codeError}</Alert>}

              <form onSubmit={handleCodeVerify}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="8-cifret kode"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  required
                  inputProps={{ maxLength: 8, inputMode: 'numeric', style: { letterSpacing: '4px', fontWeight: 'bold', textAlign: 'center' } }}
                  sx={{ mb: 2 }}
                />
                <Button fullWidth type="submit" variant="contained" size="large" disabled={codeLoading || code.length < 8}>
                  {codeLoading ? 'Verificerer...' : 'Verificér kode'}
                </Button>
              </form>
              <Button fullWidth variant="text" sx={{ mt: 2 }} onClick={() => navigate('/login')}>
                Tilbage til login
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
